import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
  Box, Flex, Text, HStack, VStack, Badge, Button, IconButton, Select, Input, Textarea,
  Spinner, Tooltip, Slider, SliderTrack, SliderFilledTrack, SliderThumb,
  useToast, FormControl, FormLabel, Checkbox,
} from '@chakra-ui/react';
import {
  FiCheckSquare, FiExternalLink, FiVolumeX, FiVolume2, FiPlay, FiSquare,
  FiPlus, FiMenu, FiAlertTriangle,
} from 'react-icons/fi';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';
import useMenuPosition from '../hooks/useMenuPosition';

const fmtDate = (d) => d ? new Date(d + 'T12:00:00').toLocaleDateString('en-GB', { day:'numeric', month:'short' }) : '';
const fmtDur = (secs) => { if (!secs) return '0m'; const h=Math.floor(secs/3600),m=Math.floor((secs%3600)/60); return h?`${h}h ${m}m`:`${m}m`; };
const secondsElapsed = (start) => Math.floor((Date.now()-new Date(start).getTime())/1000);

function DeadlineBadge({ item }) {
  if (!item.todo_deadline_date) return null;
  const dl = new Date(item.todo_deadline_date + 'T' + (item.todo_deadline_time || '23:59:59'));
  const daysLeft = Math.ceil((dl - new Date()) / 86400000);
  const leeway = item.todo_deadline_leeway_days || 0;
  const isHard = item.todo_deadline_type === 'hard';
  const eff = isHard ? daysLeft : daysLeft + leeway;
  const overdue = eff < 0, urgent = eff <= 1, warn = eff <= 3;
  return (
    <Tooltip label={`${isHard?'Hard':'Soft'} deadline: ${fmtDate(item.todo_deadline_date)}${item.todo_deadline_time?' '+item.todo_deadline_time.slice(0,5):''}${!isHard?` (${leeway}d leeway)`:''}`}>
      <Badge colorScheme={overdue?'red':urgent?'orange':warn?'yellow':'gray'} variant={overdue||urgent?'solid':'subtle'} fontSize="8px" display="flex" alignItems="center" gap={1}>
        {overdue&&<FiAlertTriangle size={9}/>}{isHard?'🔒':'📅'} {overdue?'OVERDUE':daysLeft===0?'TODAY':daysLeft===1?'TOMORROW':`${daysLeft}d`}
      </Badge>
    </Tooltip>
  );
}

function ProgressSlider({ item, isTask, onSave }) {
  const initPct = isTask ? (item.completion_percentage||0) : (item.percentage_contribution||0);
  const [pct, setPct] = useState(initPct);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(()=>{ setPct(isTask?(item.completion_percentage||0):(item.percentage_contribution||0)); setDirty(false); },[item.id, item.completion_percentage, item.percentage_contribution]); // eslint-disable-line
  return (
    <Flex align="center" gap={2} mt={1} mb={1}>
      <Slider min={0} max={100} step={5} value={pct} onChange={v=>{setPct(v);setDirty(true);}} flex={1} size="sm">
        <SliderTrack h="3px" bg="gray.100"><SliderFilledTrack bg={pct>=100?'green.400':pct>=50?'teal.400':'blue.300'}/></SliderTrack>
        <SliderThumb boxSize="10px"/>
      </Slider>
      <Text fontSize="10px" fontWeight="700" color="gray.500" w="28px">{pct}%</Text>
      {dirty&&<Button size="xs" colorScheme="teal" variant="outline" fontSize="9px" px={2} h="18px" isLoading={saving}
        onClick={async()=>{setSaving(true);await onSave(item,pct);setSaving(false);setDirty(false);}}>Save</Button>}
    </Flex>
  );
}

function ClockOffModal({ isOpen, onClose, item, session, elapsed, onSaved }) {
  const [pct, setPct] = useState(0);
  const [qualNote, setQualNote] = useState('');
  const [remaining, setRemaining] = useState('');
  const [saving, setSaving] = useState(false);
  const toast = useToast();
  useEffect(()=>{ if(isOpen&&item){setPct(item.completion_percentage||item.percentage_contribution||0);setQualNote('');setRemaining('');} },[isOpen,item]);
  const submit = async () => {
    if (!session) return;
    setSaving(true);
    try {
      await api.put(`/work-sessions/${session.id}/stop`, { progress_after: pct, notes: qualNote||null });
      if (item.item_type==='task') await api.put(`/tasks/${item.id}`, { completion_percentage: pct }).catch(()=>{});
      else await api.put(`/sub-tasks/${item.id}`, { percentage_contribution: pct }).catch(()=>{});
      const noteContent = [qualNote&&`Progress: ${qualNote}`, remaining&&`Remaining: ${remaining}`].filter(Boolean).join('\n');
      if (noteContent) await api.post('/notes', { task_id: item.item_type==='task'?item.id:item.task_id, content: noteContent, note_type: 'general' }).catch(()=>{});
      toast({ title: `Clocked off — ${fmtDur(elapsed)}`, status: 'info', duration: 2000 });
      onSaved(); onClose();
    } catch(e) { toast({ title: 'Failed to clock off', status: 'error', duration: 2000 }); }
    setSaving(false);
  };
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered>
      <ModalOverlay/><ModalContent>
        <ModalHeader fontSize="md">Clock Off — Progress Update</ModalHeader>
        <ModalCloseButton/>
        <ModalBody>
          <Text fontSize="12px" color="gray.500" mb={2} noOfLines={2}>{item?.title}</Text>
          <Badge colorScheme="green" mb={3}>Session: {fmtDur(elapsed)}</Badge>
          <VStack spacing={3} align="stretch">
            <FormControl>
              <FormLabel fontSize="11px" fontWeight="700">% Complete</FormLabel>
              <HStack spacing={3}>
                <Slider min={0} max={100} step={5} value={pct} onChange={setPct} flex={1}>
                  <SliderTrack h="6px"><SliderFilledTrack bg="teal.400"/></SliderTrack>
                  <SliderThumb boxSize="14px"/>
                </Slider>
                <Text fontSize="13px" fontWeight="800" w="36px" color="teal.600">{pct}%</Text>
              </HStack>
            </FormControl>
            <FormControl>
              <FormLabel fontSize="11px" fontWeight="700">What did you do? (qualitative)</FormLabel>
              <Textarea size="sm" rows={2} value={qualNote} onChange={e=>setQualNote(e.target.value)} placeholder="Brief description of work done..."/>
            </FormControl>
            <FormControl>
              <FormLabel fontSize="11px" fontWeight="700">What's remaining?</FormLabel>
              <Textarea size="sm" rows={2} value={remaining} onChange={e=>setRemaining(e.target.value)} placeholder="What still needs to be done..."/>
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button size="sm" variant="ghost" mr={2} onClick={onClose}>Cancel</Button>
          <Button size="sm" colorScheme="teal" isLoading={saving} onClick={submit}>Save &amp; Clock Off</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

function ProjectSearch({ onSelectProject }) {
  const [mode, setMode] = useState('company');
  const [q, setQ] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const searchCompanies = async (val) => { if(val.length<2){setSuggestions([]);return;} const{data}=await api.get(`/companies?search=${encodeURIComponent(val)}&limit=10`).catch(()=>({data:[]})); setSuggestions(data); };
  const searchProjects = async () => { if(!q)return; setLoading(true); const{data}=await api.get(`/projects?search=${encodeURIComponent(q)}&limit=20`).catch(()=>({data:[]})); setSuggestions(data); setLoading(false); };
  const pickCompany = async(co) => { setSelectedCompany(co);setQ(co.name);setSuggestions([]); const{data}=await api.get(`/projects?company_id=${co.id}&limit=100`).catch(()=>({data:[]})); setProjects(data.filter(p=>p.project_type!=='workflow_template'&&!p.is_done)); };
  const reset = () => { setQ('');setSuggestions([]);setSelectedCompany(null);setProjects([]); };
  return (
    <Box>
      <HStack mb={2} spacing={1}>
        <Button size="xs" variant={mode==='company'?'solid':'outline'} colorScheme="teal" onClick={()=>{setMode('company');reset();}}>🏢 By Company</Button>
        <Button size="xs" variant={mode==='project'?'solid':'outline'} colorScheme="teal" onClick={()=>{setMode('project');reset();}}>📁 By Project</Button>
      </HStack>
      {mode==='company'&&!selectedCompany&&(<Box position="relative"><Input size="sm" placeholder="Search company..." value={q} onChange={e=>{setQ(e.target.value);searchCompanies(e.target.value);}}/>{suggestions.length>0&&(<Box position="absolute" top="100%" left={0} right={0} bg="white" border="1px" borderColor="gray.200" borderRadius="md" shadow="md" zIndex={50} maxH="160px" overflowY="auto">{suggestions.map(co=><Box key={co.id} px={3} py={2} cursor="pointer" fontSize="12px" _hover={{bg:'teal.50'}} onClick={()=>pickCompany(co)}><Text fontWeight="600">{co.name}</Text></Box>)}</Box>)}</Box>)}
      {mode==='company'&&selectedCompany&&(<Box><HStack mb={2}><Badge colorScheme="teal">{selectedCompany.name}</Badge><Button size="xs" variant="ghost" colorScheme="red" onClick={reset}>Change</Button></HStack>{projects.length===0?<Text fontSize="11px" color="gray.400">No active projects</Text>:(<VStack spacing={1} align="stretch" maxH="180px" overflowY="auto">{projects.map(p=><Box key={p.id} px={3} py={2} bg="gray.50" border="1px" borderColor="gray.200" borderRadius="md" cursor="pointer" _hover={{bg:'teal.50'}} onClick={()=>onSelectProject(p)}><Text fontSize="12px" fontWeight="600">{p.project_name}</Text>{p.row_group_name&&<Text fontSize="10px" color="gray.400">{p.row_group_name}</Text>}</Box>)}</VStack>)}</Box>)}
      {mode==='project'&&(<Box position="relative"><HStack><Input size="sm" placeholder="Search project..." value={q} onChange={e=>setQ(e.target.value)} onKeyDown={e=>e.key==='Enter'&&searchProjects()} flex={1}/><Button size="sm" colorScheme="teal" variant="outline" onClick={searchProjects} isLoading={loading}>Search</Button></HStack>{suggestions.length>0&&(<VStack spacing={1} align="stretch" mt={2} maxH="180px" overflowY="auto">{suggestions.map(p=><Box key={p.id} px={3} py={2} bg="gray.50" border="1px" borderColor="gray.200" borderRadius="md" cursor="pointer" _hover={{bg:'teal.50'}} onClick={()=>{onSelectProject(p);reset();}}><Text fontSize="12px" fontWeight="600">{p.project_name}</Text><HStack spacing={2}><Text fontSize="10px" color="gray.400">{p.company_name||''}</Text>{p.row_group_name&&<Badge fontSize="7px" colorScheme="gray">{p.row_group_name}</Badge>}</HStack></Box>)}</VStack>)}</Box>)}
    </Box>
  );
}

function AddItemModal({ isOpen, onClose, onSaved, users }) {
  const [step, setStep] = useState('project');
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectTasks, setProjectTasks] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [itemType, setItemType] = useState('task');
  const [form, setForm] = useState({ title:'', description:'', assigned_to:'', projected_hours:'', appear_in_todo:true });
  const [saving, setSaving] = useState(false);
  const toast = useToast();
  const set = (k,v)=>setForm(p=>({...p,[k]:v}));
  const reset = ()=>{setStep('project');setSelectedProject(null);setProjectTasks([]);setSelectedTask(null);setItemType('task');setForm({title:'',description:'',assigned_to:'',projected_hours:'',appear_in_todo:true});};
  useEffect(()=>{if(!isOpen)reset();},[isOpen]); // eslint-disable-line
  const pickProject = async(p)=>{ setSelectedProject(p); const{data}=await api.get(`/tasks?project_id=${p.id}`).catch(()=>({data:[]})); setProjectTasks(data||[]); setStep('task_select'); };
  const save = async()=>{
    if(!form.title.trim()){toast({title:'Title required',status:'warning',duration:1500});return;}
    setSaving(true);
    try {
      if(itemType==='task'){const{data:t}=await api.post('/tasks',{project_id:selectedProject.id,task_title:form.title,description:form.description||null});if(form.appear_in_todo&&t?.id)await api.put(`/tasks/${t.id}/todo-appear`,{appear:true}).catch(()=>{});}
      else{await api.post('/sub-tasks',{task_id:selectedTask.id,title:form.title,description:form.description||null,appear_in_todo:form.appear_in_todo?1:0,...(form.assigned_to?{assigned_to:form.assigned_to}:{}),...(form.projected_hours?{projected_hours:parseFloat(form.projected_hours)}:{})});}
      toast({title:`${itemType==='task'?'Task':'Sub-task'} added`,status:'success',duration:1500});
      onSaved();onClose();
    }catch(e){toast({title:e.response?.data?.error||'Failed',status:'error',duration:2000});}
    setSaving(false);
  };
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered scrollBehavior="inside">
      <ModalOverlay/><ModalContent>
        <ModalHeader fontSize="md">{step==='project'?'Add Item — Select Project':step==='task_select'?`Select Task — ${selectedProject?.project_name}`:`Add ${itemType==='task'?'Task':'Sub-task'}`}</ModalHeader>
        <ModalCloseButton/>
        <ModalBody>
          {step==='project'&&<ProjectSearch onSelectProject={pickProject}/>}
          {step==='task_select'&&(<Box>
            <Button size="sm" colorScheme="teal" leftIcon={<FiPlus/>} mb={3} onClick={()=>{setSelectedTask(null);setItemType('task');setStep('form');}}>+ Add New Task</Button>
            {projectTasks.length>0&&<VStack spacing={1} align="stretch" maxH="280px" overflowY="auto">{projectTasks.map(t=><Flex key={t.id} justify="space-between" align="center" px={3} py={2} bg="blue.50" border="1px" borderColor="blue.200" borderRadius="md" cursor="pointer" _hover={{bg:'blue.100'}} onClick={()=>{setSelectedTask(t);setItemType('subtask');setStep('form');}}><Text fontSize="12px" fontWeight="600">{t.task_title}</Text><Badge colorScheme="blue" fontSize="8px">Add sub-task →</Badge></Flex>)}</VStack>}
            <Button size="xs" variant="ghost" mt={3} onClick={()=>setStep('project')}>← Back</Button>
          </Box>)}
          {step==='form'&&(<VStack spacing={3} align="stretch">
            <Box bg="gray.50" p={2} borderRadius="md" fontSize="11px"><Text color="gray.500">{itemType==='task'?'New task in':'New sub-task under'}: <b>{selectedProject?.project_name}</b>{selectedTask?` › ${selectedTask.task_title}`:''}</Text></Box>
            <FormControl isRequired><FormLabel fontSize="11px">Title</FormLabel><Input size="sm" value={form.title} onChange={e=>set('title',e.target.value)} autoFocus/></FormControl>
            <FormControl><FormLabel fontSize="11px">Description</FormLabel><Textarea size="sm" rows={2} value={form.description} onChange={e=>set('description',e.target.value)}/></FormControl>
            {itemType==='subtask'&&(<><FormControl><FormLabel fontSize="11px">Assign to</FormLabel><Select size="sm" value={form.assigned_to} onChange={e=>set('assigned_to',e.target.value)}><option value="">— Unassigned —</option>{users.map(u=><option key={u.id} value={u.id}>{u.display_name||u.username}</option>)}</Select></FormControl><FormControl><FormLabel fontSize="11px">Projected hours</FormLabel><Input size="sm" type="number" step="0.5" min="0" value={form.projected_hours} onChange={e=>set('projected_hours',e.target.value)} placeholder="e.g. 2.5"/></FormControl></>)}
            <Checkbox isChecked={form.appear_in_todo} onChange={e=>set('appear_in_todo',e.target.checked)} size="sm" colorScheme="teal"><Text fontSize="12px">Appear in To Do List</Text></Checkbox>
            <Button size="xs" variant="ghost" onClick={()=>setStep('task_select')}>← Back</Button>
          </VStack>)}
        </ModalBody>
        <ModalFooter>
          <Button size="sm" variant="ghost" mr={2} onClick={onClose}>Cancel</Button>
          {step==='form'&&<Button size="sm" colorScheme="teal" onClick={save} isLoading={saving}>Save</Button>}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default function ToDoListsDashboard({ isOpen, onClose, onNavigateToProject }) {
  const { user: authUser } = useAuth();
  const [items, setItems]   = useState([]);
  const [users, setUsers]   = useState([]);
  const [filterUser, setFilterUser] = useState('');
  const [showSilenced, setShowSilenced] = useState(false);
  const [loading, setLoading] = useState(false);
  const [contextMenu, setContextMenu] = useState(null);
  const contextMenuRef = useRef(null);
  const contextMenuPos = useMenuPosition(contextMenuRef, contextMenu);
  const [addItemOpen, setAddItemOpen] = useState(false);
  const [canSeeOthers, setCanSeeOthers] = useState(true);
  const hasDefaulted = useRef(false);
  const dragIdx = useRef(null);
  const [dragOver, setDragOver] = useState(null);
  const [activeSession, setActiveSession] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const intervalRef = useRef(null);
  const [clockOffModal, setClockOffModal] = useState(null);
  const toast = useToast();
  const isAdmin = authUser?.role === 'admin';

  useEffect(()=>{ api.get('/settings/see_others_todo').then(r=>setCanSeeOthers(r.data?.setting_value!=='false'||isAdmin)).catch(()=>setCanSeeOthers(true)); },[isAdmin]);
  useEffect(()=>{ if(isOpen&&authUser?.id&&!hasDefaulted.current){setFilterUser(authUser.id);hasDefaulted.current=true;} },[isOpen,authUser]);
  useEffect(()=>{ if(!isOpen)return; api.get('/work-sessions/active').then(r=>{if(r.data){setActiveSession(r.data);setElapsed(secondsElapsed(r.data.start_time));}}).catch(()=>{}); },[isOpen]);
  useEffect(()=>{ if(activeSession){intervalRef.current=setInterval(()=>setElapsed(secondsElapsed(activeSession.start_time)),1000);}else clearInterval(intervalRef.current); return()=>clearInterval(intervalRef.current); },[activeSession]);

  const fetchItems = useCallback(async()=>{
    setLoading(true);
    try{
      const params=new URLSearchParams({todo_only:'true'});
      if(filterUser)params.set('assigned_to',filterUser);
      if(showSilenced)params.set('show_silenced','true');
      const{data}=await api.get(`/sub-tasks/dashboard?${params}`);
      setItems(data);
    }catch(e){setItems([]);}
    finally{setLoading(false);}
  },[filterUser,showSilenced]);

  const fetchUsers=async()=>{ try{const{data}=await api.get('/calendar/users');setUsers(data);}catch(e){} };
  useEffect(()=>{ if(isOpen){fetchItems();fetchUsers();} },[isOpen,fetchItems]);
  useEffect(()=>{ if(!contextMenu)return; const close=()=>setContextMenu(null); document.addEventListener('click',close); return()=>document.removeEventListener('click',close); },[contextMenu]);

  const canReorder = !filterUser || filterUser===authUser?.id || isAdmin;

  const onDragStart=(idx)=>{ dragIdx.current=idx; };
  const onDragEnd=async()=>{
    if(dragIdx.current===null||dragOver===null||dragIdx.current===dragOver){dragIdx.current=null;setDragOver(null);return;}
    const newItems=[...items];
    const[moved]=newItems.splice(dragIdx.current,1);
    newItems.splice(dragOver,0,moved);
    setItems(newItems);
    dragIdx.current=null;setDragOver(null);
    const uid=filterUser||authUser?.id;
    const order=newItems.map((it,i)=>({item_type:it.item_type==='subtask'?'subtask':'task',item_id:it.id,position:i}));
    await api.put('/sub-tasks/todo-order',{order,user_id:uid}).catch(()=>{});
  };

  const handleToggle=async(item)=>{ try{ if(item.item_type==='task')await api.put(`/tasks/${item.id}`,{is_complete:true,completion_percentage:100}); else await api.put(`/sub-tasks/${item.id}/toggle`); await fetchItems(); toast({title:'Marked complete ✓',status:'success',duration:1500}); }catch(e){} };
  const handlePctSave=async(item,pct)=>{ try{ if(item.item_type==='task')await api.put(`/tasks/${item.id}`,{completion_percentage:pct}); else await api.put(`/sub-tasks/${item.id}`,{percentage_contribution:pct}); await fetchItems(); }catch(e){} };
  const handleSilence=async(item,silenced)=>{ try{ if(item.item_type==='task')await api.put(`/tasks/${item.id}/silence`,{silenced}); else await api.put(`/sub-tasks/${item.id}/silence`,{silenced}); await fetchItems(); }catch(e){} };
  const handleClockOn=async(item)=>{ try{ const body=item.item_type==='task'?{task_id:item.id,project_id:item.project_id,title:item.title}:{task_id:item.task_id,project_id:item.project_id,sub_task_id:item.id,title:item.title}; const{data}=await api.post('/work-sessions',body); setActiveSession(data);setElapsed(0); toast({title:'Clocked on',status:'success',duration:1500}); }catch(e){toast({title:e.response?.data?.error||'Failed',status:'error',duration:2000});} };
  const handleClockOff=(item)=>{ if(!activeSession)return; setClockOffModal({item,session:activeSession,elapsed}); };
  // Note: item.id from /sub-tasks/dashboard is raw integer (endpoint not yet UUID-rewritten).
  // activeSession.task_id/sub_task_id are UUIDs (Phase 3). This comparison will fail until
  // the dashboard endpoint is upgraded. String() removed; both sides cleanly typed.
  const isActiveItem=(item)=>{ if(!activeSession)return false; return item.item_type==='task'?activeSession.task_id===item.id:activeSession.sub_task_id===item.id; };
  const handleNavigate=(item)=>{ onClose(); if(onNavigateToProject)onNavigateToProject(item.row_group_id,item.project_id); };

  const renderItem=(item,idx)=>{
    const isTask=item.item_type==='task';
    const isSilenced=!!item.is_silenced;
    const clocked=isActiveItem(item);
    return (
      <Box key={`${item.item_type}-${item.id}`} draggable={canReorder}
        onDragStart={()=>onDragStart(idx)} onDragOver={e=>{e.preventDefault();setDragOver(idx);}} onDragEnd={onDragEnd}
        opacity={dragOver===idx?0.5:1} transition="opacity 0.1s" mb={1}>
        <Flex p={2} border="1px"
          borderColor={clocked?'teal.400':isSilenced?'gray.100':isTask?'blue.100':'gray.200'}
          borderRadius="md"
          bg={clocked?'teal.50':isSilenced?'gray.50':isTask?'blue.50':'white'}
          opacity={isSilenced?0.55:1} align="flex-start" gap={2}
          onContextMenu={e=>{e.preventDefault();e.stopPropagation();setContextMenu({x:e.clientX,y:e.clientY,item});}}>
          {canReorder&&<Box color="gray.300" cursor="grab" mt="3px" flexShrink={0} _hover={{color:'gray.500'}}><FiMenu size={13}/></Box>}
          <Box w="18px" h="18px" mt="2px" flexShrink={0} border="2px"
            borderColor={isTask?'blue.300':'gray.300'} borderRadius="sm" cursor="pointer"
            _hover={{borderColor:'green.400',bg:'green.50'}} display="flex" alignItems="center" justifyContent="center"
            onClick={()=>handleToggle(item)}/>
          <Box flex={1} minW={0}>
            <Flex justify="space-between" align="flex-start" gap={2} flexWrap="wrap">
              <HStack spacing={1} flex={1} minW={0}>
                {isTask&&<Badge colorScheme="blue" fontSize="7px" flexShrink={0}>TASK</Badge>}
                {isSilenced&&<FiVolumeX size={10} color="#A0AEC0" style={{flexShrink:0}}/>}
                <Text fontSize="12px" fontWeight={isTask?'700':'600'} color={isSilenced?'gray.400':'gray.800'} noOfLines={2}>{item.title}</Text>
              </HStack>
              <HStack spacing={1} flexShrink={0} flexWrap="wrap" justify="flex-end">
                <DeadlineBadge item={item}/>
                {item.assigned_to_name&&<Badge colorScheme="blue" fontSize="8px">{item.assigned_to_name}</Badge>}
                {item.task_held&&<Badge colorScheme="red" fontSize="8px" variant="outline">Held</Badge>}
              </HStack>
            </Flex>
            <ProgressSlider item={item} isTask={isTask} onSave={handlePctSave}/>
            <HStack mt={0.5} spacing={2} fontSize="9px" color="gray.400">
              <Text>{item.project_name}</Text>
              {!isTask&&<Text>· {item.task_title}</Text>}
              {item.todo_deadline_date&&<Text color={item.todo_deadline_type==='hard'?'orange.500':'gray.400'}>· {item.todo_deadline_type==='hard'?'🔒':'📅'} {fmtDate(item.todo_deadline_date)}</Text>}
            </HStack>
          </Box>
          <HStack spacing={0} flexShrink={0}>
            {clocked?(
              <Tooltip label={`Clock off (${fmtDur(elapsed)})`}>
                <IconButton icon={<FiSquare size={12}/>} size="xs" colorScheme="red" variant="solid" onClick={()=>handleClockOff(item)} aria-label="Clock Off"/>
              </Tooltip>
            ):(
              <Tooltip label={activeSession?'Clock off current session first':'Clock On'}>
                <IconButton icon={<FiPlay size={12}/>} size="xs" variant="ghost" colorScheme="teal"
                  isDisabled={!!activeSession&&!clocked} onClick={()=>handleClockOn(item)} aria-label="Clock On"/>
              </Tooltip>
            )}
            <Tooltip label="Go to project"><IconButton icon={<FiExternalLink size={11}/>} size="xs" variant="ghost" colorScheme="gray" onClick={()=>handleNavigate(item)} aria-label="Navigate"/></Tooltip>
          </HStack>
        </Flex>
      </Box>
    );
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="3xl" scrollBehavior="inside">
        <ModalOverlay/><ModalContent maxH="92vh">
          <ModalHeader fontSize="md">
            <Flex justify="space-between" align="center" pr={8} flexWrap="wrap" gap={2}>
              <HStack spacing={2}>
                <FiCheckSquare/><Text>To Do Lists</Text>
                <Badge colorScheme="teal" fontSize="10px">{items.length} item{items.length!==1?'s':''}</Badge>
                {activeSession&&<Badge colorScheme="green" variant="solid" fontSize="9px">● {fmtDur(elapsed)}</Badge>}
              </HStack>
              <HStack spacing={2} flexWrap="wrap">
                <Button size="xs" colorScheme="teal" leftIcon={<FiPlus/>} onClick={()=>setAddItemOpen(true)}>Add Task / Sub-task</Button>
                <HStack spacing={1} fontSize="11px" color={showSilenced?'gray.700':'gray.400'} cursor="pointer" onClick={()=>setShowSilenced(v=>!v)}>
                  {showSilenced?<FiVolume2 size={14}/>:<FiVolumeX size={14}/>}<Text>Silenced</Text>
                </HStack>
                {(canSeeOthers||isAdmin)?(
                  <Select size="xs" w="180px" value={filterUser} onChange={e=>setFilterUser(e.target.value)}>
                    <option value="">All Users</option>
                    {users.map(u=><option key={u.id} value={u.id}>{u.display_name||u.username}{u.id===authUser?.id?' (you)':''}</option>)}
                  </Select>
                ):<Badge colorScheme="gray" fontSize="10px">{authUser?.display_name||'My List'}</Badge>}
              </HStack>
            </Flex>
          </ModalHeader>
          <ModalCloseButton/>
          <ModalBody>
            {loading&&<Flex justify="center" py={6}><Spinner/></Flex>}
            {!loading&&items.length===0&&(
              <Box textAlign="center" py={10}>
                <Text fontSize="sm" color="gray.500">No To Do items found.</Text>
                <Text fontSize="11px" color="gray.400" mt={1}>Right-click a task in the main grid → "Appear in To Do List" to add it here.</Text>
                {!showSilenced&&<Text fontSize="10px" color="gray.400" mt={1}>Silenced items are hidden — toggle "Silenced" to see them.</Text>}
              </Box>
            )}
            {!loading&&items.length>0&&(
              <Box>
                {canReorder&&<Text fontSize="10px" color="gray.400" mb={2}><FiMenu size={10} style={{display:'inline',marginRight:4}}/>Drag to reorder · Right-click to silence</Text>}
                {items.map((item,idx)=>renderItem(item,idx))}
              </Box>
            )}
          </ModalBody>
          <ModalFooter>
            <HStack spacing={2} mr="auto" fontSize="10px" color="gray.400">
              <Text>{items.length} item{items.length!==1?'s':''}</Text>
              <Text>· Right-click to silence / mark complete</Text>
            </HStack>
            <Button size="sm" onClick={onClose}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {contextMenu&&(
        <Box ref={contextMenuRef} position="fixed" left={contextMenuPos.left} top={contextMenuPos.top} bg="white" border="1px" borderColor="gray.200" borderRadius="md" boxShadow="lg" zIndex={10000} py={1} minW="180px" maxH={contextMenuPos.maxHeight} maxW={contextMenuPos.maxWidth} overflowY="auto" overflowX="hidden" visibility={contextMenuPos.ready ? 'visible' : 'hidden'} onClick={e=>e.stopPropagation()}>
          <Box px={3} py={1} fontSize="10px" color="gray.400" fontWeight="600" borderBottom="1px" borderColor="gray.100" mb={0.5} noOfLines={1}>{contextMenu.item.title}</Box>
          <Box px={3} py={1.5} fontSize="12px" cursor="pointer" _hover={{bg:'green.50'}} color="green.700" display="flex" alignItems="center" gap={2} onClick={()=>{handleToggle(contextMenu.item);setContextMenu(null);}}><FiCheckSquare size={13}/> Mark as complete (100%)</Box>
          {contextMenu.item.is_silenced?(
            <Box px={3} py={1.5} fontSize="12px" cursor="pointer" _hover={{bg:'green.50'}} color="green.600" display="flex" alignItems="center" gap={2} onClick={()=>{handleSilence(contextMenu.item,false);setContextMenu(null);}}><FiVolume2 size={13}/> Un-silence</Box>
          ):(
            <Box px={3} py={1.5} fontSize="12px" cursor="pointer" _hover={{bg:'gray.50'}} color="gray.600" display="flex" alignItems="center" gap={2} onClick={()=>{handleSilence(contextMenu.item,true);setContextMenu(null);}}><FiVolumeX size={13}/> Silence</Box>
          )}
          <Box h="1px" bg="gray.100" my={0.5}/>
          <Box px={3} py={1.5} fontSize="12px" cursor="pointer" _hover={{bg:'blue.50'}} color="blue.600" display="flex" alignItems="center" gap={2} onClick={()=>{handleNavigate(contextMenu.item);setContextMenu(null);}}><FiExternalLink size={13}/> Go to project</Box>
        </Box>
      )}

      <ClockOffModal isOpen={!!clockOffModal} onClose={()=>setClockOffModal(null)} item={clockOffModal?.item} session={clockOffModal?.session} elapsed={clockOffModal?.elapsed||elapsed} onSaved={()=>{setActiveSession(null);setElapsed(0);fetchItems();}}/>
      <AddItemModal isOpen={addItemOpen} onClose={()=>setAddItemOpen(false)} onSaved={fetchItems} users={users}/>
    </>
  );
}
